import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { DynamicStore } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { apiUploadFile } from "../../../api/apiClient";
import { PaymentReceiptCreateStore } from "./PaymentReceiptCreateStore";
import { getPaymentReceiptBatchCreateVariables } from "./PaymentReceiptCommonStore";

export type BatchFile = {
  file: File;
  name: string;
  size: number;
  status: "pending" | "uploading" | "uploaded" | "error";
  uploadResult?: {
    ticket: string;
    filename: string;
    mimetype: string;
    encoding: string;
    size: number;
  };
  error?: string;
};

export class PaymentReceiptBatchCreateStore implements DynamicStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  files: BatchFile[] = [];
  status: "idle" | "uploading" | "creating" | "done" | "error" = "idle";
  result: { created: number } | null = null;
  error: string | null = null;
  acceptFilter: string | undefined = undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    makeAutoObservable(this, { rootStore: false, langStore: false });
  }

  destroy() {}

  get breadcrumbLabel(): string {
    return this.langStore.batchCreateCommand;
  }

  addFiles(newFiles: File[]) {
    for (const file of newFiles) {
      this.files.push({
        file,
        name: file.name,
        size: file.size,
        status: "pending",
      });
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  clearFiles() {
    this.files = [];
  }

  reset() {
    this.files = [];
    this.status = "idle";
    this.result = null;
    this.error = null;
  }

  *executeBatchCreate(createStore: PaymentReceiptCreateStore): any {
    // Validate shared fields
    const errors = createStore.validateData(createStore.entityValues);
    createStore.errors = { ...errors };
    if (Object.keys(errors).length > 0) {
      createStore.showValidationErrorsDialog();
      return;
    }

    if (this.files.length === 0) {
      this.error = "No files selected";
      return;
    }

    try {
      // Phase 1: Upload files
      this.status = "uploading";
      this.error = null;

      for (let i = 0; i < this.files.length; i++) {
        const batchFile = this.files[i];
        if (batchFile.status === "uploaded" && batchFile.uploadResult) continue;

        runInAction(() => {
          batchFile.status = "uploading";
        });
        try {
          const data = yield apiUploadFile(
            true,
            batchFile.file,
            this.rootStore.activityStore
          );
          runInAction(() => {
            batchFile.uploadResult = data.singleUpload;
            batchFile.status = "uploaded";
          });
        } catch (err: any) {
          runInAction(() => {
            batchFile.status = "error";
            batchFile.error = err?.message || "Upload failed";
          });
          this.status = "error";
          this.error = `Error uploading ${batchFile.name}`;
          return;
        }
      }

      // Phase 2: Call batch create mutation
      this.status = "creating";

      const sharedVariables = getPaymentReceiptBatchCreateVariables(
        createStore.entityValues
      );
      const files = this.files.map((f) => f.uploadResult!);

      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.paymentReceiptBatchCreate({
        ...sharedVariables,
        files,
      });

      this.result = { created: result.paymentReceiptBatchCreate.created };
      this.status = "done";

      // Clear pending changes and navigate back
      (createStore as any)._ownPendingChanges = false;
      this.rootStore.router.goBack();
    } catch (error: any) {
      this.status = "error";
      // Este store muestra el error inline en vez de abrir el diálogo. Para la
      // sesión vencida eso no alcanza: el texto dice "volvé a entrar" pero no
      // da cómo. Mismo trato que el resto de los stores.
      if (isSessionExpired(error, this.langStore)) {
        this.rootStore.authStore.openSessionExpiredDialog(() => {});
        return;
      }
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.error = apiError.message;
      } else {
        this.error = this.langStore.serverError;
      }
    }
  }
}
